import React, { useState } from 'react';

function TimelineView({ timeline = [], onTimelineChange = null }) {
  const [editIdx, setEditIdx] = useState(null);
  const [editTime, setEditTime] = useState('');
  const [editEvent, setEditEvent] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newTime, setNewTime] = useState('');
  const [newEvent, setNewEvent] = useState('');

  const isEditable = !!onTimelineChange;

  if (!timeline || timeline.length === 0) {
    if (!isEditable) return null;
    return (
      <div className="ff-card" data-testid="timeline-view">
        <h2>Timeline</h2>
        <p className="ff-card-description">No timeline events yet.</p>
        <button className="ff-btn-secondary ff-btn-compact ff-mt-sm" onClick={() => setShowAdd(true)} data-testid="timeline-add-btn">+ Add Event</button>
        {showAdd && renderAddForm()}
      </div>
    );
  }

  function startEdit(idx) {
    setEditIdx(idx);
    setEditTime(timeline[idx].time || '');
    setEditEvent(timeline[idx].event || '');
  }

  function saveEdit() {
    if (editIdx === null || !onTimelineChange) return;
    const updated = [...timeline];
    updated[editIdx] = { ...updated[editIdx], time: editTime, event: editEvent };
    onTimelineChange(updated);
    setEditIdx(null);
  }

  function cancelEdit() { setEditIdx(null); }

  function deleteRow(idx) {
    if (!onTimelineChange) return;
    const updated = timeline.filter((_, i) => i !== idx);
    onTimelineChange(updated);
  }

  function moveRow(idx, dir) {
    if (!onTimelineChange) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= timeline.length) return;
    const updated = [...timeline];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    onTimelineChange(updated);
  }

  function addEvent() {
    if (!newEvent.trim() || !onTimelineChange) return;
    const updated = [...timeline, { time: newTime || 'Not specified', event: newEvent, approximate: true }];
    onTimelineChange(updated);
    setNewTime('');
    setNewEvent('');
    setShowAdd(false);
  }

  function renderAddForm() {
    return (
      <div className="ff-inline-form" data-testid="timeline-add-form">
        <input
          placeholder="Time (e.g. 9:30 AM)"
          value={newTime}
          onChange={e => setNewTime(e.target.value)}
          className="ff-dark-input ff-mb-sm"
          data-testid="timeline-new-time"
        />
        <input
          placeholder="What happened"
          value={newEvent}
          onChange={e => setNewEvent(e.target.value)}
          className="ff-dark-input ff-mb-md"
          data-testid="timeline-new-event"
        />
        <div className="ff-flex-row">
          <button className="ff-btn-primary ff-btn-xs" onClick={addEvent} data-testid="timeline-add-save">Add</button>
          <button className="ff-btn-secondary ff-btn-xs" onClick={() => setShowAdd(false)}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ff-card" data-testid="timeline-view">
      <div className="ff-flex-row--spread">
        <div>
          <h2>Timeline</h2>
          <p className="ff-card-description">Chronological sequence of events extracted from your narrative.</p>
        </div>
        {isEditable && (
          <button className="ff-btn-secondary ff-btn-compact" onClick={() => setShowAdd(!showAdd)} data-testid="timeline-add-btn">
            + Add Event
          </button>
        )}
      </div>

      <div className="ff-timeline">
        {timeline.map((item, index) => (
          <div key={index} className="ff-timeline-item" data-testid={`timeline-row-${index}`}>
            <div className="ff-timeline-marker">
              <div className="ff-timeline-dot"></div>
              {index < timeline.length - 1 && <div className="ff-timeline-line"></div>}
            </div>
            {editIdx === index ? (
              <div className="ff-timeline-content" style={{ flex: 1 }}>
                <input value={editTime} onChange={e => setEditTime(e.target.value)} data-testid={`timeline-edit-time-${index}`}
                  className="ff-dark-input ff-dark-input--editing ff-mb-sm" />
                <input value={editEvent} onChange={e => setEditEvent(e.target.value)} data-testid={`timeline-edit-event-${index}`}
                  className="ff-dark-input ff-dark-input--editing ff-mb-md" />
                <div className="ff-edit-actions">
                  <button className="ff-btn-primary ff-btn-xs" onClick={saveEdit} data-testid={`timeline-save-${index}`}>Save</button>
                  <button className="ff-btn-secondary ff-btn-xs" onClick={cancelEdit}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="ff-timeline-content" style={{ flex: 1 }}>
                <div className="ff-timeline-time">
                  {item.time || 'Time not specified'}
                  {item.approximate && <span className="ff-approx">(approx.)</span>}
                </div>
                <div className="ff-timeline-event">{item.event}</div>
                {isEditable && (
                  <div className="ff-edit-actions">
                    <button onClick={() => startEdit(index)} data-testid={`timeline-edit-btn-${index}`}
                      className="ff-action-btn ff-action-btn--edit">Edit</button>
                    <button onClick={() => deleteRow(index)} data-testid={`timeline-delete-btn-${index}`}
                      className="ff-action-btn ff-action-btn--delete">Delete</button>
                    {index > 0 && <button onClick={() => moveRow(index, -1)}
                      className="ff-action-btn ff-action-btn--muted">Up</button>}
                    {index < timeline.length - 1 && <button onClick={() => moveRow(index, 1)}
                      className="ff-action-btn ff-action-btn--muted">Down</button>}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {showAdd && renderAddForm()}
    </div>
  );
}

export default TimelineView;
